// Airtable-style dense grid for inventory.
// - 1 row per SKU × 4 location columns + today-delta + actions
// - Click a stock cell → inline popover to log event at that location
// - Click SKU name → slide-over with full details
// - Sticky header, no zebra (DESIGN.md: row borders only), hover, tabular figures
// - Cells + name + mobile rows are keyboard-operable (role=button, Enter/Space)
// - On mobile (< 768px) falls back to a compact card list + bottom drawer
//
// All visual rules live in src/styles/inventory-grid.css. This file should
// contain ZERO inline style={{}} blocks (one numerical exception: popover
// position which needs runtime top/left).

import { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../lib/api.ts';
import { displayName } from '../lib/products.ts';
import { useIsMobile } from '../lib/useMediaQuery.js';
import { LOCATIONS as LOCS, STOREFRONTS, KITCHENS, locationLabel, isKitchen } from '../lib/locations.ts';

const ACTION_LABEL = { produced: '生产', sold: '售出', wasted: '报废', transferred: '调拨', adjust: '改写', reverse_produce: '撤销生产' };
// Full label map (covers legacy reasons for display of historical rows).
const WASTE_REASON_LABEL = {
  expired: '过期', damaged: '损坏', overproduction: '多产', display: '展示',
  donated: '捐献', staff_taken: '店员取走', other: '其他'
};
// Reasons OFFERED for new 报废 entries (Regina, 2026-05-29; 其他 added 2026-06-03).
const WASTE_ENTRY_REASONS = ['damaged', 'donated', 'staff_taken', 'other'];

// 店员取走 picker value scheme — keeps staff ids and custom-taker ids unambiguous
// in a single <select>. '__new__' reveals a free-text input for a brand-new name.
export const NEW_TAKER_VALUE = '__new__';
export function parseTakerValue(v) {
  if (v === NEW_TAKER_VALUE) return { kind: 'new' };
  if (typeof v === 'string' && v.startsWith('staff:'))  return { kind: 'staff',  id: v.slice(6) };
  if (typeof v === 'string' && v.startsWith('custom:')) return { kind: 'custom', id: v.slice(7) };
  return { kind: 'none' };
}

const SHIFT_TAG = {
  day:   { cls: 'day',   text: '白' },
  night: { cls: 'night', text: '晚' },
  both:  null
};

// Today's date in local YMD (for filtering events).
function todayLocalYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Aggregate today's events by (productId, location) → { produced, sold, wasted }.
// Used to render the "+12 / -3 / -1" today-delta column.
function aggregateTodayDeltas(events) {
  const today = todayLocalYMD();
  const by = new Map(); // key: productId, value: { produced, sold, wasted }
  for (const e of events || []) {
    if (!e.createdAt) continue;
    // D1 emits UTC; convert to local YMD for the "today" bucket.
    const d = new Date(e.createdAt.replace(' ', 'T') + (e.createdAt.endsWith('Z') ? '' : 'Z'));
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (ymd !== today) continue;
    let agg = by.get(e.productId);
    if (!agg) { agg = { produced: 0, sold: 0, wasted: 0 }; by.set(e.productId, agg); }
    if (e.kind === 'produced')      agg.produced += e.qty;
    else if (e.kind === 'sold')     agg.sold     += Math.abs(e.qty);
    else if (e.kind === 'wasted')   agg.wasted   += Math.abs(e.qty);
  }
  return by;
}

// Aggregate today's events by (productId, location) → { plus, minus }.
// Drives the per-cell "+当日新增 / −当日减少" second line. plus = sum of
// positive qty (produced/received/transfer-in/positive adjust); minus = abs
// sum of negative qty (sold/wasted/transfer-out/negative adjust).
function aggregateCellDeltas(events) {
  const today = todayLocalYMD();
  const by = new Map(); // key: `${productId}|${location}` → { plus, minus }
  for (const e of events || []) {
    if (!e.createdAt || !e.location) continue;
    if (e.kind === 'adjusted') continue;   // 改写/重设 are corrections, not 今日变动 flow
    const d = new Date(e.createdAt.replace(' ', 'T') + (e.createdAt.endsWith('Z') ? '' : 'Z'));
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (ymd !== today) continue;
    const key = `${e.productId}|${e.location}`;
    let agg = by.get(key);
    if (!agg) { agg = { plus: 0, minus: 0 }; by.set(key, agg); }
    if (e.qty > 0) agg.plus += e.qty;
    else if (e.qty < 0) agg.minus += Math.abs(e.qty);
  }
  return by;
}

export const InventoryGrid = ({
  products,                // GridRow[] from /inventory/grid
  thresholdMap,            // Map<"productId|location", threshold>
  recentEvents,            // InventoryEvent[] — fallback today-delta source
  dayDeltas,               // Map<productId,{produced,sold,wasted}> — server aggregate for the active day (preferred)
  cellDeltaMap,            // Map<"productId|location",{plus,minus}> — server per-cell +/− for the selected day (preferred over client scan)
  deltaLabel = '今日变动',  // column header — '今日变动' on today, '当日变动' when viewing a past day
  search,                  // string filter
  sortKey,                 // 'name' | 'category' | 'total' | 'today_produced' | 'today_wasted'
  customOrder,             // string[] — per-user pinned product ids (float to top in this order)
  lowOnly = false,         // boolean — when true, show ONLY below-threshold rows
  staff,                   // {id,name}[] — for the 店员取走 picker
  customTakers,            // {id,name}[] — recalled non-staff names for the 店员取走 picker
  onLogEvent,              // ({productId, kind, location, qty, reason?, taken_by?}) => Promise
  onTransfer,              // ({productId, from, to, qty}) => Promise
  onAdjust,                // ({productId, location, target, reason?, note}) => Promise — 店长以上 改写
  onReverseProduce,        // ({productId, location, qty, note}) => Promise — 店长以上 撤销生产
  canAdjust,               // boolean — actor is 店长以上 (isElevated)
  onOpenDetail             // (productId) => void — opens slide-over
}) => {
  // openCell: which stock cell has the inline popover open (or null).
  // Mobile / desktop split — render only the active one (was rendering both
  // and toggling display:none, which doubled DOM weight for 47 SKUs).
  const isMobile = useIsMobile();

  const [openCell, setOpenCell] = useState(null);
  // anchorRect: bounding box of the clicked cell, so popover knows where to render.
  const [anchorRect, setAnchorRect] = useState(null);

  // Prefer the server-computed day aggregate (accurate, windowed by occurred_at,
  // works for past days). Fall back to the client-side scan of recent events
  // only when no aggregate is supplied.
  // Pinned product ids — render a small marker so a user (or a second person on
  // a shared tablet) can tell which rows are floated to the top by *their*
  // custom order, vs merely sorted high.
  const pinnedSet = useMemo(() => new Set(customOrder || []), [customOrder]);

  const todayBy = useMemo(
    () => dayDeltas || aggregateTodayDeltas(recentEvents),
    [dayDeltas, recentEvents]
  );

  // Set of product ids with at least one location below its threshold. Drives
  // both the 低库存 filter and the float-to-top pin (low rows are the action
  // items, so they sort above the rest — after the user's own pinned order).
  const lowRowSet = useMemo(() => {
    const s = new Set();
    for (const p of (products || [])) {
      for (const loc of LOCS) {
        const th = thresholdMap?.get(`${p.id}|${loc}`);
        if (th !== undefined && (p.balances[loc] || 0) < th) { s.add(p.id); break; }
      }
    }
    return s;
  }, [products, thresholdMap]);

  // Per-cell +/− deltas. Prefer the server aggregate (occurred_at-windowed for
  // the selected day, complete — works on any 补录 day). Fall back to the
  // client scan of recentEvents only when the map isn't supplied yet.
  const cellDeltas = useMemo(
    () => cellDeltaMap || aggregateCellDeltas(recentEvents),
    [cellDeltaMap, recentEvents]
  );

  // Filter + sort the products list. Memoized so typing in search doesn't
  // re-aggregate today's events on every keystroke.
  const rows = useMemo(() => {
    let r = products || [];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.variant  || '').toLowerCase().includes(q)
      );
    }
    if (lowOnly) r = r.filter(p => lowRowSet.has(p.id));
    const totalOf = (p) => LOCS.reduce((s, l) => s + (p.balances[l] || 0), 0);
    const todayOf = (p, k) => todayBy.get(p.id)?.[k] || 0;
    const cmp = {
      name:           (a, b) => a.name.localeCompare(b.name, 'zh'),
      category:       (a, b) => (a.category || '').localeCompare(b.category || '', 'zh')
                                  || a.name.localeCompare(b.name, 'zh'),
      total:          (a, b) => totalOf(b) - totalOf(a),
      today_produced: (a, b) => todayOf(b, 'produced') - todayOf(a, 'produced'),
      today_sold:     (a, b) => todayOf(b, 'sold')     - todayOf(a, 'sold'),
      today_wasted:   (a, b) => todayOf(b, 'wasted')   - todayOf(a, 'wasted')
    };
    const base = cmp[sortKey] || cmp.category;
    // Per-user pinned products float to the top in their saved order; the rest
    // keep the chosen sort. A single comparator keeps it stable + cheap.
    const rank = new Map((customOrder || []).map((id, i) => [id, i]));
    return [...r].sort((a, b) => {
      const ar = rank.has(a.id) ? rank.get(a.id) : Infinity;
      const br = rank.has(b.id) ? rank.get(b.id) : Infinity;
      if (ar !== br) return ar - br;          // both pinned → by saved order; one pinned → it wins
      // Among non-pinned rows, low-stock floats to the top (action items first).
      const al = lowRowSet.has(a.id) ? 0 : 1;
      const bl = lowRowSet.has(b.id) ? 0 : 1;
      if (al !== bl) return al - bl;
      return base(a, b);                       // neither pinned/low-tiered → normal sort
    });
  }, [products, search, sortKey, todayBy, customOrder, lowOnly, lowRowSet]);

  function openPopover(productId, location, evt, defaultAction) {
    const rect = evt.currentTarget.getBoundingClientRect();
    setAnchorRect(rect);
    setOpenCell({ productId, location, defaultAction });
  }
  function closePopover() { setOpenCell(null); setAnchorRect(null); }

  if (!products) {
    return <GridSkeleton />;
  }
  if (rows.length === 0) {
    if (lowOnly && !search) {
      return (
        <div className="inv-grid-wrap">
          <div className="inv-empty">
            <div className="emoji">✅</div>
            <div className="title">没有低于阈值的产品</div>
            <div className="sub">库存都在安全线以上 · 再点一次「低库存」看全部</div>
          </div>
        </div>
      );
    }
    return search ? (
      <div className="inv-grid-wrap">
        <div className="inv-empty">
          <div className="emoji">🔍</div>
          <div className="title">没有匹配「{search}」的产品</div>
          <div className="sub">试试搜「草莓」「6寸」「胚」</div>
        </div>
      </div>
    ) : (
      <div className="inv-grid-wrap">
        <div className="inv-empty">
          <div className="emoji">📦</div>
          <div className="title">还没有产品</div>
          <div className="sub">去「管理产品」添加 SKU，或切换班次视图</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop grid — only rendered on > 768px viewports */}
      {!isMobile && (
        <div className="inv-grid-wrap">
          <table className="inv-grid">
            <colgroup>
              <col style={{ width: '32%' }} />
              <col style={{ width: '14%' }} />
              {LOCS.map(l => <col key={l} style={{ width: '8%' }} />)}
              <col style={{ width: '15%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>产品</th>
                <th>品类</th>
                {LOCS.map(l => <th key={l} className="num">{locationLabel(l)}</th>)}
                <th className="num" title="生产 / 售出 / 报废">{deltaLabel}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const t = todayBy.get(p.id) || { produced: 0, sold: 0, wasted: 0 };
                const tag = SHIFT_TAG[p.shift];
                return (
                  <tr key={p.id}>
                    <td className="inv-name" role="button" tabIndex={0}
                        onClick={() => onOpenDetail(p.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(p.id); } }}
                        aria-label={`${displayName(p)} · 查看详情`}
                        title={p.name}>
                      {pinnedSet.has(p.id) && <span className="inv-pin-mark" title="已置顶（你的常用）">📌</span>}
                      {displayName(p)}
                      {tag && <span className={`inv-shift-tag ${tag.cls}`}>{tag.text}</span>}
                    </td>
                    <td className="inv-name-sub">{p.category || '—'}</td>
                    {LOCS.map(loc => {
                      const qty = p.balances[loc] || 0;
                      const threshold = thresholdMap?.get(`${p.id}|${loc}`);
                      const low = threshold !== undefined && qty < threshold;
                      const zero = qty === 0;
                      const cd = cellDeltas.get(`${p.id}|${loc}`);
                      return (
                        <td key={loc}
                            role="button" tabIndex={0}
                            className={`inv-cell-num ${low ? 'low' : ''} ${zero ? 'zero' : ''}`}
                            onClick={(e) => openPopover(p.id, loc, e)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPopover(p.id, loc, e); } }}
                            aria-label={`${displayName(p)} ${locationLabel(loc)} 库存 ${qty}${low ? '，低于阈值' : ''} · 回车录入`}
                            title={threshold !== undefined ? `阈值 ${threshold} · 点击录入` : '点击录入'}>
                          <span className="inv-cell-total">{qty}</span>
                          {cd && (cd.plus > 0 || cd.minus > 0) && (
                            <span className="inv-cell-delta">
                              {cd.plus > 0 && <span className="cd-plus">+{cd.plus}</span>}
                              {cd.minus > 0 && <span className="cd-minus">−{cd.minus}</span>}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="inv-delta">
                      <span className={t.produced ? 'd-p' : 'd-z'}>+{t.produced}</span>
                      <span className={t.sold     ? 'd-s' : 'd-z'}>−{t.sold}</span>
                      <span className={t.wasted   ? 'd-w' : 'd-z'}>×{t.wasted}</span>
                    </td>
                    <td className="inv-row-actions">
                      <button onClick={() => onOpenDetail(p.id)} title="详情">⋯</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile compact list — only rendered on ≤ 768px viewports */}
      {isMobile && (
        <div className="inv-mobile-list">
          {rows.map(p => {
            const total = LOCS.reduce((s, l) => s + (p.balances[l] || 0), 0);
            return (
              <div key={p.id} className="inv-mobile-row"
                   role="button" tabIndex={0}
                   onClick={() => onOpenDetail(p.id)}
                   onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(p.id); } }}
                   aria-label={`${displayName(p)} · 查看详情`}>
                <div className="name" title={p.name}>
                  {pinnedSet.has(p.id) && <span className="inv-pin-mark" title="已置顶">📌</span>}
                  {p.category && <span className="inv-name-sub">{p.category} · </span>}
                  {displayName(p)}
                </div>
                <div className="breakdown">
                  {LOCS.map(l => `${locationLabel(l)} ${p.balances[l] || 0}`).join(' · ')}
                </div>
                <div className="total">{total}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline popover for stock-cell editing */}
      {openCell && anchorRect && (
        <CellPopover
          cell={openCell}
          anchor={anchorRect}
          product={rows.find(p => p.id === openCell.productId)}
          staff={staff}
          customTakers={customTakers}
          onLog={onLogEvent}
          onTransfer={onTransfer}
          onAdjust={onAdjust}
          onReverseProduce={onReverseProduce}
          canAdjust={canAdjust}
          onClose={closePopover}
        />
      )}
    </>
  );
};

// ============================================================================
// Inline popover — appears next to the clicked stock cell.
// ============================================================================

const CellPopover = ({ cell, anchor, product, staff, customTakers, onLog, onTransfer, onAdjust, onReverseProduce, canAdjust, onClose }) => {
  // Default action based on the location clicked:
  //   - kitchen clicked → likely 生产 (production happens at CK_RH / CK_NY)
  //   - storefront clicked → likely 售出 (sales happen at storefronts)
  // The user can override with the action buttons.
  const defaultAction = cell.defaultAction
    || (isKitchen(cell.location) ? 'produced' : 'sold');
  const [action, setAction] = useState(defaultAction);
  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState('damaged');
  const [wasteNote, setWasteNote] = useState('');   // free-text for reason='other'
  const staffList = staff || [];
  const takerList = customTakers || [];
  // 店员取走 picker: a single prefixed value (staff:<id> / custom:<id> / __new__)
  // plus the free-text name shown when __new__ is selected.
  const [takerValue, setTakerValue] = useState(
    staffList[0] ? `staff:${staffList[0].id}` : NEW_TAKER_VALUE
  );
  const [newTakerName, setNewTakerName] = useState('');
  const [transferTo, setTransferTo] = useState(
    LOCS.find(l => l !== cell.location) || 'RH'
  );
  // 改写 (override) state — absolute target + required reason note.
  const curBalance = product?.balances?.[cell.location] || 0;
  const [target, setTarget] = useState(String(curBalance));
  const [adjustNote, setAdjustNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const isAdjust = action === 'adjust';
  const isReverse = action === 'reverse_produce';   // 撤销生产 (店长+, kitchen only)

  // Auto-focus the qty input + select-all on open so the user can just type
  // the number immediately.
  useEffect(() => {
    if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, []);

  // Edit presence: heartbeat that I'm editing this product, learn who else is.
  // If someone else has it open, show a conflict warning so two people don't
  // log the same product's stock at once. Polls every 15s; expires server-side.
  const [coEditors, setCoEditors] = useState([]);
  useEffect(() => {
    let stopped = false;
    const resource = `product:${cell.productId}`;
    async function beat() {
      try {
        const others = await api.postInventoryPresence(resource);
        if (!stopped) setCoEditors(others);
      } catch { /* presence is best-effort */ }
    }
    beat();
    const iv = setInterval(beat, 15000);
    return () => { stopped = true; clearInterval(iv); };
  }, [cell.productId]);

  // Esc + outside click close the popover.
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    function onDown(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  // Position the popover below the clicked cell, clamped to viewport.
  const top  = Math.min(anchor.bottom + 6, window.innerHeight - 240);
  const left = Math.min(anchor.left, window.innerWidth - 300);

  async function submit() {
    // 改写 (override): absolute target + required reason note, 店长以上 only.
    if (isAdjust) {
      const tgt = parseInt(target, 10);
      if (!Number.isFinite(tgt) || tgt < 0) { setErr('目标数量必须 ≥ 0'); return; }
      if (!adjustNote.trim()) { setErr('请填写改写原因'); return; }
      setErr(''); setSubmitting(true);
      try {
        await onAdjust({
          productId: cell.productId, location: cell.location,
          target: tgt, reason: 'correction', note: adjustNote.trim()
        });
        onClose();
      } catch (e) {
        setErr(e?.message || '失败');
        setSubmitting(false);
      }
      return;
    }

    // 撤销生产 (reverse production): positive amount to reverse + required reason,
    // 店长以上 only. Server writes a negative `produced` event that nets 生产 down.
    if (isReverse) {
      const n = parseInt(qty, 10);
      if (!Number.isFinite(n) || n <= 0) { setErr('撤销数量必须 > 0'); return; }
      if (!adjustNote.trim()) { setErr('请填写撤销原因'); return; }
      setErr(''); setSubmitting(true);
      try {
        await onReverseProduce({
          productId: cell.productId, location: cell.location,
          qty: n, note: adjustNote.trim()
        });
        onClose();
      } catch (e) {
        setErr(e?.message || '失败');
        setSubmitting(false);
      }
      return;
    }

    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) { setErr('数量必须 > 0'); return; }
    setErr(''); setSubmitting(true);
    try {
      if (action === 'transferred') {
        if (transferTo === cell.location) { setErr('源/目的不能相同'); setSubmitting(false); return; }
        await onTransfer({ productId: cell.productId, from: cell.location, to: transferTo, qty: n });
      } else if (action === 'produced') {
        // Produced is kitchen-only; the cell IS a kitchen (the action is only
        // enabled on CK_RH / CK_NY cells), so log at this cell's location.
        await onLog({ productId: cell.productId, kind: 'produced', location: cell.location, qty: n });
      } else {
        const payload = { productId: cell.productId, kind: action, location: cell.location, qty: n };
        if (action === 'wasted') {
          payload.reason = reason;
          if (reason === 'staff_taken') {
            const sel = parseTakerValue(takerValue);
            if (sel.kind === 'staff') {
              payload.taken_by = sel.id;
              payload.takenByName = staffList.find(s => s.id === sel.id)?.name || sel.id;
            } else if (sel.kind === 'custom') {
              payload.taken_by_custom_id = sel.id;
              payload.takenByName = takerList.find(t => t.id === sel.id)?.name || '';
            } else if (sel.kind === 'new') {
              const nm = newTakerName.trim();
              if (!nm) { setErr('请输入取走人姓名'); setSubmitting(false); return; }
              payload.new_taker_name = nm;
              payload.takenByName = nm;
            } else {
              setErr('请选择或填写取走人'); setSubmitting(false); return;
            }
          }
          if (reason === 'other') {
            if (!wasteNote.trim()) { setErr('请填写备注'); setSubmitting(false); return; }
            payload.note = wasteNote.trim();
          }
        }
        await onLog(payload);
      }
      onClose();
    } catch (e) {
      setErr(e?.message || '失败');
      setSubmitting(false);
    }
  }

  // Action availability rules:
  //   - 售出 only valid at storefronts (RH/DT/NY)
  //   - 生产 only valid at a kitchen (CK_RH / CK_NY)
  const actions = [
    { key: 'produced',    enabled: isKitchen(cell.location) },
    { key: 'sold',        enabled: STOREFRONTS.includes(cell.location) },
    { key: 'wasted',      enabled: true },
    { key: 'transferred', enabled: true }
  ];

  return (
    <div className="inv-popover"
         ref={popoverRef}
         style={{ top, left }}
         onClick={(e) => e.stopPropagation()}>
      <div className="inv-popover-head">
        <strong>{product ? displayName(product) : cell.productId}</strong>
        <span>@ {locationLabel(cell.location)}</span>
      </div>
      {coEditors.length > 0 && (
        <div className="inv-popover-coedit" role="status">
          ⚠️ {coEditors.map(e => e.name).join('、')} 正在编辑这个产品 · 注意别重复录入
        </div>
      )}
      <div className="inv-popover-actions">
        {actions.map(a => (
          <button key={a.key}
                  className={action === a.key ? 'active' : ''}
                  disabled={!a.enabled}
                  onClick={() => setAction(a.key)}>
            {ACTION_LABEL[a.key]}
          </button>
        ))}
      </div>
      {/* 改写 (override) — 店长以上 only, set as its own muted row to keep it
          visually distinct from the everyday actions. */}
      {canAdjust && (
        <button
          className={`inv-popover-adjust-row ${isAdjust ? 'active' : ''}`}
          onClick={() => setAction(isAdjust ? defaultAction : 'adjust')}>
          ✎ 改写库存 · 仅店长以上
        </button>
      )}
      {/* 撤销生产 — 店长以上 only, kitchen cells only. Nets the 生产 tally DOWN
          (unlike 改写, which corrects balance but leaves 生产 overstated). */}
      {canAdjust && isKitchen(cell.location) && (
        <button
          className={`inv-popover-adjust-row ${isReverse ? 'active' : ''}`}
          onClick={() => setAction(isReverse ? defaultAction : 'reverse_produce')}>
          ↩ 撤销生产 · 仅店长以上
        </button>
      )}
      {!isAdjust && action === 'wasted' && (
        <div className="inv-popover-reason">
          {WASTE_ENTRY_REASONS.map(k => (
            <button key={k}
                    className={reason === k ? 'active' : ''}
                    onClick={() => setReason(k)}>
              {WASTE_REASON_LABEL[k]}
            </button>
          ))}
        </div>
      )}
      {action === 'wasted' && reason === 'staff_taken' && (
        <>
          <div className="inv-popover-reason">
            <span className="vava-hint inv-hint-gap">取走人</span>
            <select className="vava-input inv-popover-staff"
                    value={takerValue}
                    onChange={(e) => setTakerValue(e.target.value)}>
              {staffList.length > 0 && (
                <optgroup label="员工">
                  {staffList.map(s => <option key={s.id} value={`staff:${s.id}`}>{s.name}</option>)}
                </optgroup>
              )}
              {takerList.length > 0 && (
                <optgroup label="其他">
                  {takerList.map(t => <option key={t.id} value={`custom:${t.id}`}>{t.name}</option>)}
                </optgroup>
              )}
              <option value={NEW_TAKER_VALUE}>✏️ 其他（手动输入）…</option>
            </select>
          </div>
          {parseTakerValue(takerValue).kind === 'new' && (
            <div className="inv-popover-note">
              <input className="vava-input inv-popover-note-input"
                     type="text" value={newTakerName} maxLength={40}
                     aria-label="取走人姓名" autoFocus
                     placeholder="输入姓名"
                     onChange={(e) => setNewTakerName(e.target.value)} />
            </div>
          )}
        </>
      )}
      {action === 'wasted' && reason === 'other' && (
        <div className="inv-popover-note">
          <input className="vava-input inv-popover-note-input"
                 type="text" value={wasteNote} maxLength={200}
                 aria-label="报废备注"
                 placeholder="备注（必填）· 例如 客诉退货"
                 onChange={(e) => setWasteNote(e.target.value)} />
        </div>
      )}
      {action === 'transferred' && (
        <>
          <div className="inv-popover-reason">
            <span className="vava-hint inv-hint-gap">调到</span>
            {LOCS.filter(l => l !== cell.location).map(l => (
              <button key={l}
                      title={locationLabel(l)}
                      className={transferTo === l ? 'active' : ''}
                      onClick={() => setTransferTo(l)}>
                {locationLabel(l)}
              </button>
            ))}
          </div>
          <div className="inv-popover-sellout">
            <span className="vava-hint">当前库存 {curBalance}</span>
            <button type="button" className="inv-popover-sellout-btn"
                    disabled={curBalance <= 0}
                    onClick={() => setQty(String(curBalance))}>
              一键调拨
            </button>
          </div>
        </>
      )}
      {action === 'sold' && (
        <div className="inv-popover-sellout">
          <span className="vava-hint">当前库存 {curBalance}</span>
          <button type="button" className="inv-popover-sellout-btn"
                  disabled={curBalance <= 0}
                  onClick={() => setQty(String(curBalance))}>
            一键售罄
          </button>
        </div>
      )}
      {isAdjust ? (
        <div className="inv-popover-adjust">
          <div className="inv-popover-adjust-target">
            <span className="vava-hint">改为</span>
            <input ref={inputRef}
                   type="number" min="0" step="1"
                   value={target}
                   onChange={(e) => setTarget(e.target.value)} />
          </div>
          <div className="inv-popover-adjust-preview">
            {(() => {
              const tgt = parseInt(target, 10);
              if (!Number.isFinite(tgt)) return <span className="vava-hint">当前 {curBalance}</span>;
              const delta = tgt - curBalance;
              return (
                <span className="vava-hint">
                  {curBalance} → {tgt}
                  {delta !== 0 && (
                    <span className={delta > 0 ? 'cd-plus' : 'cd-minus'}>
                      {' '}({delta > 0 ? '+' : '−'}{Math.abs(delta)})
                    </span>
                  )}
                </span>
              );
            })()}
          </div>
          <input className="vava-input inv-popover-adjust-note"
                 type="text"
                 placeholder="改写原因（必填）"
                 value={adjustNote}
                 onChange={(e) => setAdjustNote(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        </div>
      ) : (
        <>
        <div className="inv-popover-qty">
          <button className="step" onClick={() => setQty(String(Math.max(1, (parseInt(qty, 10) || 0) - 1)))}>−</button>
          <input ref={inputRef}
                 type="number" min="1" step="1"
                 value={qty}
                 onChange={(e) => setQty(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
          <button className="step" onClick={() => setQty(String((parseInt(qty, 10) || 0) + 1))}>+</button>
        </div>
        {isReverse && (
          <div className="inv-popover-adjust">
            <div className="inv-popover-adjust-preview">
              {(() => {
                const n = parseInt(qty, 10);
                const valid = Number.isFinite(n) && n > 0;
                return (
                  <span className="vava-hint">
                    {curBalance}{valid && <> → {curBalance - n} <span className="cd-minus">(−{n})</span></>}
                  </span>
                );
              })()}
            </div>
            <input className="vava-input inv-popover-adjust-note"
                   type="text"
                   placeholder="撤销原因（必填）"
                   value={adjustNote}
                   onChange={(e) => setAdjustNote(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
          </div>
        )}
        </>
      )}
      {err && <div className="inv-popover-err">{err}</div>}
      <div className="inv-popover-foot">
        <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>取消</button>
        <button className="btn btn-primary" onClick={submit} disabled={submitting}>
          {submitting ? '记录中…' : `确认${ACTION_LABEL[action]}`}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Skeleton loader — shows the grid shape while data is fetching.
// ============================================================================

const GridSkeleton = () => (
  <div className="inv-grid-wrap">
    <table className="inv-grid">
      <thead>
        <tr>
          <th>产品</th><th>品类</th>
          {LOCS.map(l => <th key={l} className="num">{l}</th>)}
          <th className="num">今日变动</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 8 }).map((_, i) => (
          <tr key={i}>
            <td><span className="inv-skeleton w-lg" /></td>
            <td><span className="inv-skeleton w-md" /></td>
            {LOCS.map(l => <td key={l} className="inv-cell-num"><span className="inv-skeleton w-sm" /></td>)}
            <td className="inv-delta"><span className="inv-skeleton w-md" /></td>
            <td></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
